import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { gotoLogin } from '@/features/Functions/ResultFunctions';
import CrownIcon   from '@/features/Icons/CrownIcon';

import { HtmlTooltip } from './ToolTip';

// Define the type for each option
interface DropdownOption {
  value: string;
  label: React.ReactNode; // Allow JSX elements
  icon?: React.ReactNode;
  isPremium?: boolean;
  tooltip?: React.ReactNode;
}

// Define the props for the Dropdown component
interface DropdownProps {
  label: string;
  value: string;
  onChange: (e: SelectChangeEvent) => void;
  options: DropdownOption[];
  premium?: boolean;
}

const Dropdown = styled(({ label, value, onChange, options, premium, ...props }: DropdownProps) => (
  <FormControl {...props} size="small">
    <InputLabel>{label}</InputLabel>
    <Select
      value={value}
      onChange={onChange}
      label={label}
      sx={{
        backgroundColor: 'white',
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
        minHeight: '36px',
        padding: '4px',
      }}
    >
      {options.map((option) =>
        option.isPremium && !premium ? (
          <HtmlTooltip
            key={option.value}
            title={
              <React.Fragment>
                <Typography variant="body2" color="inherit">
                  <CrownIcon /> {option.tooltip}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <FormattedMessage id="home.text-generator.premium" />
                </Typography>
                <Link
                  href="#"
                  color="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    gotoLogin();
                  }}
                >
                  <FormattedMessage id="home.text-generator.signupnow" />
                </Link>
              </React.Fragment>
            }
          >
            <span>
              <MenuItem
                value={option.value}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  minHeight: '30px',
                  padding: '4px 8px',
                  pointerEvents: option.isPremium && !premium ? 'none' : 'auto',
                  whiteSpace: 'normal', // Allow text to wrap
                  overflow: 'hidden', // Prevent overflow
                  textOverflow: 'ellipsis', // Add ellipsis for overflow (optional)
                  width: '100%', // Ensure MenuItem takes full width
                }}
              >
                <Typography sx={{ color: option.isPremium && !premium ? 'text.disabled' : 'inherit' }}>
                  {option.label}
                </Typography>
                <Box sx={{ opacity: 1, color: 'inherit' }}>{option.icon}</Box>
              </MenuItem>
            </span>
          </HtmlTooltip>
        ) : (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              minHeight: '30px',
              padding: '4px 8px',
              whiteSpace: 'normal', // Allow text to wrap
              overflow: 'hidden', // Prevent overflow
              textOverflow: 'ellipsis', // Add ellipsis for overflow (optional)
              width: '100%', // Ensure MenuItem takes full width
              display: 'block', // Ensure the content behaves as a block element
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem', width: '100%' }}>
              {option.icon}
              <Typography
                sx={{
                  flex: 1, // Allow the text to take remaining space
                  overflow: 'hidden', // Prevent overflow
                  textOverflow: 'ellipsis', // Add ellipsis for overflow
                  whiteSpace: 'nowrap', // Prevent text from wrapping (optional, use if you want truncation)
                }}
              >
                {option.label}
              </Typography>
            </Box>
          </MenuItem>
        )
      )}
    </Select>
  </FormControl>
))({
  width: '100%',
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiSelect-root': {
    fontSize: '0.875rem',
  },
});

export default Dropdown;


